import EmptyState from "@/components/clubs/empty-state";
import InvitationCard from "@/components/clubs/invitation-card";
import { ProfilePictureSelector } from "@/components/profile-picture-selector";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  useChangePassword,
  useCurrentUser,
  useSendVerificationEmail,
  useUnlinkStrava,
} from "@/data/auth/hooks";
import {
  useClubInvitations,
  useHandleClubInvitation,
  useLeaveClub,
  useUserClubs,
} from "@/data/clubs/hooks";
import { useAuthStore } from "@/data/useAuthStore";
import { useUpdateUserAvatar, useUpdateUserProfile } from "@/data/users/hooks";
import { initiateLinkWithStrava } from "@/lib/strava-utils";
import { cn } from "@/lib/utils";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  CheckCircle,
  Loader2,
  LogOut,
  RefreshCw,
  Settings,
  Shield,
  User,
  Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { getClubIconById } from "../lib/club-utils";

export const Route = createFileRoute("/settings")({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();

  const activeClubId = useAuthStore((store) => store.activeClubId);
  const setActiveClubId = useAuthStore((store) => store.setActiveClubId);
  const { data: user } = useCurrentUser();

  // Club invitations
  const {
    data: invitations,
    isLoading: isInvitationsLoading,
    isError: isInvitationsError,
  } = useClubInvitations();
  const handleInvitation = useHandleClubInvitation();

  const [isStravaConnected, setIsStravaConnected] = useState<boolean>(false);
  const [showStravaDisconnectDialog, setShowStravaDisconnectDialog] =
    useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [clubToLeave, setClubToLeave] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [lastEmailSentTime, setLastEmailSentTime] = useState<number | null>(
    null
  );

  // Fetch user clubs
  const { data: userClubs } = useUserClubs();
  const leaveClubMutation = useLeaveClub();

  const [formValues, setFormValues] = useState({
    firstName: "",
    lastName: "",
    weight: 0,
  });

  // State for password change
  const [passwordValues, setPasswordValues] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const updateProfileMutation = useUpdateUserProfile();
  const updateAvatarMutation = useUpdateUserAvatar();
  const sendVerificationEmailMutation = useSendVerificationEmail();
  const changePasswordMutation = useChangePassword();

  const handleLogout = () => {
    setActiveClubId(null);
    localStorage.removeItem("auth_token");
    navigate({ to: "/login", reloadDocument: true });
  };

  // Reset password fields
  const resetPasswordFields = () => {
    setPasswordValues({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setPasswordError(null);
  };

  // Handle password input changes
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle password change
  const handleChangePassword = () => {
    setPasswordError(null);

    // Validate passwords
    if (passwordValues.newPassword.length < 8) {
      setPasswordError("New password must be at least 8 characters long");
      return;
    }

    if (passwordValues.newPassword !== passwordValues.confirmPassword) {
      setPasswordError("New passwords do not match");
      return;
    }

    // Submit password change
    changePasswordMutation.mutate(
      {
        currentPassword: passwordValues.currentPassword,
        newPassword: passwordValues.newPassword,
      },
      {
        onSuccess: () => {
          toast.success("Password changed successfully");
          resetPasswordFields();
        },
        onError: (error) => {
          setPasswordError(error.message || "Failed to change password");
        },
      }
    );
  };

  const handleImageChange = (image: string | null) => {
    setSelectedImage(image);
  };

  // Function to convert a base64 image to a File object
  const base64ToFile = (base64String: string): File => {
    const arr = base64String.split(",");
    const mime = arr[0].match(/:(.*?);/)![1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], "profile-image." + mime.split("/")[1], {
      type: mime,
    });
  };

  const saveProfileChanges = () => {
    setIsLoading(true);

    // Update profile information
    updateProfileMutation.mutate(
      {
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        weight: formValues.weight,
      },
      {
        onSuccess: () => {
          toast.success("Profile updated successfully");
        },
        onError: (error) => {
          toast.error("Failed to update profile", {
            description: error.message,
          });
        },
      }
    );

    // Update avatar if selected
    if (selectedImage) {
      const file = base64ToFile(selectedImage);
      updateAvatarMutation.mutate(file, {
        onSuccess: () => {
          toast.success("Profile picture updated");
          setSelectedImage(null);
        },
        onError: (error) => {
          toast.error("Failed to update profile picture", {
            description: error.message,
          });
        },
      });
    }

    setIsLoading(false);
  };

  useEffect(() => {
    // Initialize form values when user data is loaded
    if (user) {
      setFormValues({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        weight: user.weight || 0,
      });

      // Set selected image if user has an avatar
      if (user.avatarUrl) {
        setSelectedImage(user.avatarUrl);
      }

      // Set Strava connection status
      setIsStravaConnected(!!user.stravaId);
    }
  }, [user]);

  // Check if the verification button should be disabled due to cooldown
  const isVerificationButtonDisabled = () => {
    if (!lastEmailSentTime) return false;
    const cooldownPeriod = 60 * 1000; // 60 seconds in milliseconds
    return Date.now() - lastEmailSentTime < cooldownPeriod;
  };

  // Get remaining cooldown time in seconds
  const getRemainingCooldownTime = () => {
    if (!lastEmailSentTime) return 0;
    const cooldownPeriod = 60 * 1000; // 60 seconds
    const elapsed = Date.now() - lastEmailSentTime;
    return Math.max(0, Math.ceil((cooldownPeriod - elapsed) / 1000));
  };

  const sendVerificationEmail = () => {
    sendVerificationEmailMutation.mutate(undefined, {
      onSuccess: () => {
        setLastEmailSentTime(Date.now());
        toast.success("Verification email sent", {
          description:
            "Please check your inbox and follow the link to verify your email.",
        });
      },
      onError: (error) => {
        toast.error("Failed to send verification email", {
          description: error.message,
        });
      },
    });
  };

  const toggleStravaConnection = () => {
    // For connecting to Strava
    if (!isStravaConnected) {
      initiateLinkWithStrava();
    }
  };

  const unlinkStravaMutation = useUnlinkStrava();

  const disconnectStravaAccount = async () => {
    try {
      await unlinkStravaMutation.mutateAsync();
      toast.success("Strava account disconnected");
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to disconnect Strava account"
      );
    }
  };

  const openStravaProfile = () => {
    if (user?.stravaId) {
      window.open(`https://www.strava.com/athletes/${user.stravaId}`, "_blank");
    }
  };

  const getInitials = () => {
    if (!user) return "";
    const firstInitial = user.firstName ? user.firstName.charAt(0) : "";
    const lastInitial = user.lastName ? user.lastName.charAt(0) : "";
    return (firstInitial + lastInitial).toUpperCase();
  };

  // Handle weight input change with validation
  const handleWeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Allow empty string for UX purposes
    if (value === "") {
      setFormValues((prev) => ({ ...prev, weight: 0 }));
      return;
    }

    // Check if the input is a valid number
    let numValue = parseFloat(value);
    if (isNaN(numValue)) return;

    if (numValue >= 200) {
      numValue = 200;
    }

    // Limit to 2 decimal places and reasonable weight range
    if (numValue >= 0 && numValue <= 200) {
      setFormValues((prev) => ({
        ...prev,
        weight: Math.round(numValue * 100) / 100,
      }));
    }
  };

  return (
    <div className="container max-w-2xl p-4">
      <div className="flex flex-col gap-8 max-w-3xl mx-auto">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-[#2b7fff] to-[#fe9a00] bg-clip-text text-transparent">
            Account Settings
          </h1>
          <Button
            variant="destructive"
            onClick={() => setShowLogoutDialog(true)}
            className="gap-2"
          >
            <LogOut size={16} />
            Logout
          </Button>
        </div>

        {/* Confirm Logout Dialog */}
        <ConfirmDialog
          open={showLogoutDialog}
          onOpenChange={setShowLogoutDialog}
          title="Confirm Logout"
          description="Are you sure you want to log out of your account?"
          confirmLabel="Logout"
          confirmVariant="destructive"
          onConfirm={handleLogout}
          emoji="👋"
          message="You will need to log in again to access your account."
        />

        {/* Leave Club Confirmation Dialog */}
        {clubToLeave && (
          <ConfirmDialog
            open={clubToLeave !== null}
            onOpenChange={(open) => !open && setClubToLeave(null)}
            title="Leave Club"
            description={`Are you sure you want to leave ${clubToLeave.name}?`}
            confirmLabel="Leave Club"
            confirmVariant="destructive"
            onConfirm={() => {
              // Explicitly provide the clubId parameter
              leaveClubMutation.mutate(
                { clubId: clubToLeave.id },
                {
                  onSuccess: () => {
                    toast.success("Left club", {
                      description: `You have left ${clubToLeave.name}`,
                    });
                    setClubToLeave(null);

                    // If the active club is the one being left, clear it
                    if (activeClubId === clubToLeave.id) {
                      setActiveClubId(null);
                    }
                  },
                  onError: (error) => {
                    toast.error("Failed to leave club", {
                      description: error.message,
                    });
                    setClubToLeave(null);
                  },
                }
              );
            }}
            emoji="😢"
            message="This action cannot be undone. You will need to be invited again to rejoin."
            disabled={leaveClubMutation.isPending}
          />
        )}

        {/* Profile Management */}
        <Card className="shadow-md border-t-4 border-t-blue-500">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <User size={18} className="text-blue-500" />
              <CardTitle>Profile Management</CardTitle>
            </div>
            <CardDescription>
              Update your personal information and profile picture
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-8">
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <ProfilePictureSelector
                currentImage={user?.avatarUrl}
                onImageChange={handleImageChange}
                initials={getInitials()}
              />

              <div className="flex-1 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      placeholder="John"
                      value={formValues.firstName}
                      onChange={(e) =>
                        setFormValues((prev) => ({
                          ...prev,
                          firstName: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      placeholder="Doe"
                      value={formValues.lastName}
                      onChange={(e) =>
                        setFormValues((prev) => ({
                          ...prev,
                          lastName: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="weight">
                    Weight (kg)
                    <span className="text-xs text-muted-foreground ml-1">
                      (optional, used in calories burned estimations)
                    </span>
                  </Label>
                  <Input
                    id="weight"
                    type="number"
                    step="0.01"
                    min="0"
                    max="500"
                    placeholder="70"
                    value={formValues.weight || ""}
                    onChange={handleWeightChange}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="flex items-center gap-2">
                    Email
                    {user?.isVerified ? (
                      <Badge
                        variant="outline"
                        className="bg-green-50 text-green-700 border-green-200 flex items-center gap-1"
                      >
                        <CheckCircle className="h-3 w-3" />
                        Verified
                      </Badge>
                    ) : (
                      <Badge
                        variant="outline"
                        className="bg-amber-50 text-amber-700 border-amber-200 flex items-center gap-1"
                      >
                        <Shield className="h-3 w-3" />
                        Unverified
                      </Badge>
                    )}
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="email"
                      type="email"
                      value={user?.email || ""}
                      disabled
                      className="flex-1"
                    />
                    {!user?.isVerified && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="whitespace-nowrap"
                        onClick={sendVerificationEmail}
                        disabled={
                          sendVerificationEmailMutation.isPending ||
                          isVerificationButtonDisabled()
                        }
                      >
                        {sendVerificationEmailMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-1" />
                        ) : isVerificationButtonDisabled() ? (
                          <RefreshCw className="h-4 w-4 mr-1" />
                        ) : (
                          <Shield className="h-4 w-4 mr-1" />
                        )}
                        {isVerificationButtonDisabled()
                          ? `Resend in ${getRemainingCooldownTime()}s`
                          : "Verify Email"}
                      </Button>
                    )}
                  </div>
                  {!user?.isVerified && (
                    <p className="text-xs text-muted-foreground">
                      Please verify your email to unlock all features.
                    </p>
                  )}
                </div>
              </div>
            </div>

            <Button
              onClick={saveProfileChanges}
              disabled={isLoading}
              className="w-full sm:w-auto"
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </CardContent>
        </Card>

        {/* Connected Accounts */}
        <Card className="shadow-md border-t-4 border-t-blue-500">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-5 h-5 text-blue-500"
              >
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </svg>
              <CardTitle>Integrations</CardTitle>
            </div>
            <CardDescription>
              Manage connections with third-party services
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg border p-4 bg-blue-50 dark:bg-blue-950 transition-all hover:shadow-md">
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-0 sm:items-center sm:justify-between">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <svg viewBox="0 0 24 24" className="h-8 w-8" fill="#FC4C02">
                      <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169" />
                    </svg>
                  </div>
                  <div className="max-w-[calc(100%-40px)] sm:max-w-[280px] md:max-w-[320px]">
                    <h4 className="text-sm font-medium">Connect with Strava</h4>
                    {user && user.isVerified === false ? (
                      <p className="text-xs text-amber-600 pr-2">
                        Please verify your email first.
                      </p>
                    ) : (
                      <p
                        className={`text-xs ${isStravaConnected ? "text-green-600" : "text-muted-foreground"}`}
                      >
                        {isStravaConnected
                          ? "Connected. Activities will upload automatically."
                          : "Link to automatically upload future activities."}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex-shrink-0 self-end sm:self-auto">
                  {isStravaConnected ? (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 border-blue-200"
                        onClick={openStravaProfile}
                      >
                        View
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                        onClick={() => setShowStravaDisconnectDialog(true)}
                      >
                        Disconnect
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="default"
                      size="sm"
                      className={cn(
                        "bg-blue-500 transition-colors",
                        !user?.isVerified
                          ? "opacity-50 cursor-not-allowed"
                          : "hover:bg-blue-600"
                      )}
                      onClick={toggleStravaConnection}
                      disabled={!user || !user.isVerified}
                    >
                      Connect
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Security Settings */}
        <Card className="shadow-md border-t-4 border-t-blue-500">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-500" />
              <CardTitle>Security</CardTitle>
            </div>
            <CardDescription>
              Manage your password and account security
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="currentPassword">Current Password</Label>
                <Input
                  id="currentPassword"
                  name="currentPassword"
                  type="password"
                  value={passwordValues.currentPassword}
                  onChange={handlePasswordChange}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <Input
                  id="newPassword"
                  name="newPassword"
                  type="password"
                  value={passwordValues.newPassword}
                  onChange={handlePasswordChange}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={passwordValues.confirmPassword}
                  onChange={handlePasswordChange}
                />
              </div>

              {passwordError && (
                <p className="text-sm text-red-500">{passwordError}</p>
              )}
            </div>

            <Button
              onClick={handleChangePassword}
              disabled={changePasswordMutation.isPending}
              variant="outline"
            >
              {changePasswordMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Change Password
            </Button>
          </CardContent>
        </Card>

        {/* My Clubs Section */}
        <Card className="shadow-md border-t-4 border-t-blue-500">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-500" />
              <CardTitle>My Clubs</CardTitle>
            </div>
            <CardDescription>Manage your club memberships</CardDescription>
          </CardHeader>
          <CardContent>
            {userClubs && userClubs.length > 0 ? (
              <div className="space-y-4">
                {userClubs.map((club) => {
                  const IconComponent = getClubIconById(club.icon);

                  return (
                    <div
                      key={club.id}
                      className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                          <IconComponent className="h-4 w-4" />
                        </div>
                        <div>
                          <h4 className="font-medium">{club.name}</h4>
                          <p className="text-sm text-muted-foreground line-clamp-1">
                            {club.description || "No description"}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                          onClick={() => navigate({ to: "/club-management" })}
                          disabled={!club.isAdmin}
                          title={
                            club.isAdmin
                              ? "Manage club settings"
                              : "Only admins can manage club settings"
                          }
                        >
                          <Settings className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setClubToLeave(club)}
                          disabled={club.isFounder}
                          title={
                            club.isFounder
                              ? "Founders cannot leave their club"
                              : "Leave this club"
                          }
                        >
                          <LogOut className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground">
                <p>You are not a member of any clubs yet.</p>
                <Button
                  variant="link"
                  onClick={() => navigate({ to: "/home" })}
                  className="mt-2"
                >
                  Go to the Clubs page to join one.
                </Button>
              </div>
            )}

            {/* Club Invitations */}
            <div className="mt-6">
              {isInvitationsLoading ? (
                <EmptyState
                  title="Loading..."
                  description="Fetching your club invitations."
                />
              ) : isInvitationsError ? (
                <EmptyState
                  title="Error"
                  description="Could not load invitations."
                />
              ) : invitations &&
                invitations.filter((inv) => inv.status === "PENDING").length >
                  0 ? (
                <div className="flex flex-col gap-4">
                  {invitations
                    .filter((inv) => inv.status === "PENDING")
                    .map((invitation: any) => (
                      <InvitationCard
                        key={invitation.id}
                        clubName={invitation.club.name}
                        icon={Users}
                        inviterName={`${invitation.inviter.firstName} ${invitation.inviter.lastName}`}
                        date={
                          invitation.createdAt
                            ? new Date(
                                invitation.createdAt
                              ).toLocaleDateString()
                            : ""
                        }
                        onAccept={() =>
                          handleInvitation.mutate({
                            invitationId: invitation.id,
                            action: "accept",
                          })
                        }
                        onDecline={() =>
                          handleInvitation.mutate({
                            invitationId: invitation.id,
                            action: "decline",
                          })
                        }
                      />
                    ))}
                </div>
              ) : (
                <EmptyState
                  title="No invitations"
                  description="You don't have any club invitations at the moment."
                />
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Strava Disconnect Confirmation Dialog */}
      <ConfirmDialog
        open={showStravaDisconnectDialog}
        onOpenChange={setShowStravaDisconnectDialog}
        title="Disconnect Strava"
        description="Are you sure you want to disconnect your Strava account?"
        emoji="🚴"
        confirmLabel="Disconnect"
        confirmVariant="destructive"
        message="Your Strava activities will no longer sync with Miles."
        onConfirm={disconnectStravaAccount}
      />
    </div>
  );
}
